import * as ng from 'angular';
import TasksService from '../TasksService';
import AppConstants from '../../gorillaboy/AppConstants';

import {Task, Action, TaskCommentMessage} from '../../types/TaskTypes';
import {TasksMessageManager} from "../TasksMessageManager";
import {ITaskDefinitionHelper} from "../TaskDefinitionHelper";
import {IAuthenticatedUserService} from "../../types/ServiceTypes";

import '../TasksMessageManager';
import '../TaskDefinitionHelper';

export interface IScope extends ng.IScope {
    formData: {
        commentMessage: string;
        commentTextAreaErrorMesssage: string;
    };
    actionBtnClicked: (action: Action) => void;
    showCommentTextarea: boolean;
    task: Task;
    taskDefHelper: ITaskDefinitionHelper;
    taskComments: TaskCommentMessage[];
    userId2Name: object;
    showTaskComments: (task: Task) => boolean;
    $close(): void;
    $dismiss(): void;
}

angular.module('GorillaBoy')
    .controller('taskDetailsModalCtrl', [
        '$scope',
        '$log',
        '$translate',
        'task',
        'showCommentTextarea',
        'TasksService',
        'TasksMessageManager',
        'TaskDefinitionHelper',
        'MessageManager',
        'View',
        '$q',
        'authenticatedUser',
        'UserCollectionService',
        function taskDetailsModalCtrl($scope: IScope, $log: ng.ILogService, $translate, task: Task,
                                      showCommentTextarea: boolean,
                                      TasksService: TasksService, TasksMessageManager: TasksMessageManager,
                                      TaskDefinitionHelper: ITaskDefinitionHelper, MessageManager: any,
                                      View : string,
                                      $q : ng.IQService, authenticatedUser: IAuthenticatedUserService, UserCollectionService: any) {
            // Initialization
            $scope.formData = {
                commentMessage: '',
                commentTextAreaErrorMesssage: ''
            };
            $scope.task = task;
            $scope.showCommentTextarea = showCommentTextarea;
            $scope.taskDefHelper = TaskDefinitionHelper;
            $scope.taskComments = getTaskComments(task);
            $scope.userId2Name = {};

            setUserId2Name(task);

            // Definition of scope methods
            $scope.validateCommentMessage = function() {
                if ($scope.formData.commentMessage.trim() !== '') {
                    $scope.formData.commentTextAreaErrorMesssage = '';
                }
            };

            $scope.showCommentButton = function() {
                // As per spec, only contact admin shows the commenting button
                return task.TaskDefinitionId === AppConstants.TASKDEF_IDS.NOTIFY_ADMIN_TO_DO;
            };

            /* This function shows textArea if it is now shown.
             * It add the comment if user has provided any comment
             */
            $scope.onAddCommentButtonClicked = function() {
                if (!$scope.showCommentTextarea) {
                    $scope.showCommentTextarea = true;
                } else {
                    if ($scope.formData.commentMessage) {
                        addComment($scope.formData.commentMessage).then(function() {
                            $scope.formData.commentMessage = '';
                            $scope.showCommentTextarea = false;
                            $scope.taskComments = getTaskComments(task);
                            MessageManager.addSuccessMessage($translate.instant('TASKS.DETAIL_VIEW.COMMENT_ADDED_SUCCEEDED'));
                        }, function(error) {
                            MessageManager.addErrorMessage($translate.instant('TASKS.DETAIL_VIEW.COMMENT_ADDED_FAILED'));
                            $log.error('Error adding comment to: ' + task.Id + 'with error: ' + JSON.stringify(error));
                        });
                    } else {
                        $scope.formData.commentTextAreaErrorMesssage = $translate.instant('TASKS.DETAIL_VIEW.ERROR_MESSAGES.EMPTY_TEXTAREA');
                    }
                }
            };

            $scope.showTaskComments = function(task: Task) {
                return task.MetadataParsed && task.MetadataParsed[AppConstants.Tasks.MetadataFields.ADDITIONAL_COMMENT];
            };

            $scope.actionBtnClicked = function (action : Action) {
                if (TaskDefinitionHelper.isDenyAction(task.TaskDefinitionId, action.ID)
                    && !$scope.showCommentTextarea) {
                    $scope.showCommentTextarea = true;
                    return;
                }

                //Internal actions include things like redirections to other views
                if (TaskDefinitionHelper.isInternalAction(task, action.ID)) {
                    TaskDefinitionHelper.takeInternalAction(task, action.ID);
                    $scope.$dismiss();
                    return $q.when(true);
                }

                let finalPromise;
                if ($scope.formData.commentMessage) {
                    // If there is comment message, add comment then do the action 
                    finalPromise = addComment($scope.formData.commentMessage).then(takeAction);
                } else {
                    //If there's no deny message or it's not a Deny action, then you don't have to update the Task metadata
                    //The following is the orchestrator logic for this situation
                    finalPromise = takeAction();
                }
                finalPromise.then(showSuccessMessage, showErrorMessage);

                function takeAction() {
                    return TasksService.takeAction(task.Id, action.ID);
                }

                function showSuccessMessage() {
                    TasksMessageManager.showSuccessMessageForTaskAction(task, action.ID);
                    $scope.$close();
                }

                function showErrorMessage(error) {
                    $log.error("Failed to perform action on task: " + task.Id + " with error: " + JSON.stringify(error));
                    TasksMessageManager.showErrorMessageForTaskAction(task, action.ID);
                    $scope.$dismiss();

                    //if we don't return a rejected promise from the rejection handler, the rejection won't propagate
                    //to derived promises.
                    return $q.reject(error);
                }
            };

            // Definition of private methods
            function addComment(comment) {
                return $q((resolve, reject) => {
                    if (!comment) {
                        return resolve();
                    }
                    let newMetadata = {};
                    let newAdditionalComments = [];

                    // get new metadata
                    if (task.Metadata) {
                        try {
                            newMetadata = JSON.parse(task.Metadata);
                        } catch (e) {
                            $log.error(`Error parsing the metadata for Task ${task.Id} with metadata >${task.Metadata}<`);
                        }
                    }

                    // make taskCommentMessage
                    let commentMessage: TaskCommentMessage = {
                        Author: authenticatedUser.getUserInfo().Id,
                        CreatedTimestamp: new Date().getTime(),
                        message: comment
                    }

                    // get new additional comments
                    if (newMetadata[AppConstants.Tasks.MetadataFields.ADDITIONAL_COMMENT]) {
                        newAdditionalComments = JSON.parse(newMetadata[AppConstants.Tasks.MetadataFields.ADDITIONAL_COMMENT]);
                    }
                    newAdditionalComments.push(commentMessage);

                    // Add new addtional comments to the new metadata
                    // Notice: the value of the metadata has to be a string!
                    newMetadata[AppConstants.Tasks.MetadataFields.ADDITIONAL_COMMENT] = JSON.stringify(newAdditionalComments);

                    TasksService.updateTaskMetadata(task.Id, JSON.stringify(newMetadata)).then((data) => {
                        task.MetadataParsed = newMetadata;
                        task.Metadata = JSON.stringify(newMetadata);
                        resolve(data);
                    }, reject);
                });
            }

            function getTaskComments(task: Task) {
                if (!task.MetadataParsed || !task.MetadataParsed[AppConstants.Tasks.MetadataFields.ADDITIONAL_COMMENT]) {
                    return [];
                }
                return JSON.parse(task.MetadataParsed[AppConstants.Tasks.MetadataFields.ADDITIONAL_COMMENT]);
            }

            function setUserId2Name(task: Task) {
                let userIds = getTaskComments(task).map((comment) => {
                    return comment.Author;
                });
                if (userIds) {
                    UserCollectionService.getUserCollection(userIds).then(function(response) {
                        response.forEach((userInfo) => {
                            $scope.userId2Name[userInfo.Id] = $scope.getFullName(userInfo);
                        });
                    });
                }
            }
        }
    ]);
